import React, { useState, useEffect } from 'react';
import {
  Users, Plus, Edit3, Trash2, Save, X, UserPlus,
  Mail, Phone, AlertTriangle, CheckCircle, Search,
  Filter, Download, Upload, Settings, Heart, Utensils
} from 'lucide-react';
import { staffService } from '../utils/supabaseStaffService';
import { logger } from '../utils/logger';

const StaffManagement = ({ currentUser: currentUserProp }) => {
  const [staffMembers, setStaffMembers] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form state for adding/editing staff
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    dietaryRestrictions: [],
    favoriteCuisines: [],
    allergens: [],
    notes: ''
  });

  const commonRoles = [
    'Medical Assistant',
    'Nurse',
    'Receptionist',
    'Office Manager',
    'Medical Secretary',
    'Lab Technician',
    'Physician Assistant',
    'Other'
  ];

  const dietaryOptions = [
    'Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free', 'Keto', 
    'Paleo', 'Low-sodium', 'Diabetic-friendly', 'Heart-healthy'
  ];

  const cuisineOptions = [
    'Italian', 'Mediterranean', 'Japanese', 'Chinese', 'Mexican', 
    'American', 'Indian', 'Thai', 'French', 'Greek', 'Korean', 'Vietnamese'
  ];

  const allergenOptions = [
    'Nuts', 'Shellfish', 'Fish', 'Eggs', 'Milk', 'Soy', 'Wheat', 'Sesame'
  ];

  useEffect(() => {
    loadData();
  }, [currentUserProp]);

  useEffect(() => {
    filterStaff();
  }, [staffMembers, searchTerm, filterRole]);

  const loadData = async () => {
    if (!currentUserProp?.id) {
      logger.warn('StaffManagement: No current user provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      logger.debug('StaffManagement: Loading staff for physician', {
        physicianId: currentUserProp.id,
        isActingOnBehalf: currentUserProp.isActingOnBehalf,
        actualUserType: currentUserProp.actualUserType
      });

      const staff = await staffService.getStaffForPhysician(currentUserProp.id);
      logger.debug('StaffManagement: Loaded staff members', { count: staff.length });
      setStaffMembers(staff);
    } catch (error) {
      logger.error('StaffManagement: Error loading staff', { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const filterStaff = () => {
    let filtered = staffMembers.filter(staff => staff.is_active !== false);

    if (searchTerm) {
      filtered = filtered.filter(staff =>
        staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (staff.email && staff.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filterRole) {
      filtered = filtered.filter(staff => staff.role === filterRole);
    }

    setFilteredStaff(filtered);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      email: '',
      phone: '',
      dietaryRestrictions: [],
      favoriteCuisines: [],
      allergens: [],
      notes: ''
    });
    setEditingStaff(null);
    setShowAddForm(false);
  };

  const handleArrayToggle = (array, item, setArray) => {
    if (array.includes(item)) {
      setArray(array.filter(i => i !== item));
    } else {
      setArray([...array, item]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.role) {
      setSaveStatus('Error: Name and role are required');
      setTimeout(() => setSaveStatus(''), 4000);
      return;
    }

    if (!currentUserProp?.id) {
      setSaveStatus('Error: No physician ID available');
      setTimeout(() => setSaveStatus(''), 4000);
      return;
    }

    try {
      let result;
      if (editingStaff) {
        // Update existing staff member
        result = await staffService.updateStaffMember(editingStaff.id, formData);
      } else {
        // Add new staff member
        result = await staffService.addStaffMember(currentUserProp.id, formData);
      }

      if (result) {
        setSaveStatus(editingStaff ? 'Staff member updated successfully!' : 'Staff member added successfully!');
        loadData(); // Reload staff list
        resetForm();
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        setSaveStatus('Error saving staff member. Please try again.');
        setTimeout(() => setSaveStatus(''), 4000);
      }
    } catch (error) {
      logger.error('Error saving staff member:', error);
      setSaveStatus('Error saving staff member. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    }
  };

  const handleEdit = (staff) => {
    setFormData({
      name: staff.name,
      role: staff.role,
      email: staff.email || '',
      phone: staff.phone || '',
      dietaryRestrictions: staff.dietary_restrictions || [],
      favoriteCuisines: staff.favorite_cuisines || [],
      allergens: staff.allergens || [],
      notes: staff.notes || ''
    });
    setEditingStaff(staff);
    setShowAddForm(true);
  };

  const handleDelete = (staff) => {
    setStaffToDelete(staff);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!staffToDelete) return;

    try {
      const success = await staffService.deleteStaffMember(staffToDelete.id);
      if (success) {
        setSaveStatus(`${staffToDelete.name} removed from staff list.`);
        loadData();
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        setSaveStatus('Error removing staff member. Please try again.');
        setTimeout(() => setSaveStatus(''), 4000);
      }
    } catch (error) {
      logger.error('Error deleting staff member:', error);
      setSaveStatus('Error removing staff member. Please try again.');
      setTimeout(() => setSaveStatus(''), 4000);
    }
    
    setStaffToDelete(null);
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setStaffToDelete(null);
    setShowDeleteConfirm(false);
  };

  const exportStaffList = () => {
    const csvData = [
      ['Name', 'Role', 'Email', 'Phone', 'Dietary Restrictions', 'Favorite Cuisines', 'Allergens', 'Notes'],
      ...filteredStaff.map(staff => [
        staff.name,
        staff.role,
        staff.email || '',
        staff.phone || '',
        staff.dietary_restrictions?.join('; ') || '',
        staff.favorite_cuisines?.join('; ') || '',
        staff.allergens?.join('; ') || '',
        staff.notes || ''
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-list-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!currentUserProp) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">No User Data</h3>
          <p className="text-yellow-600">Unable to load staff management. Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Staff Management</h2>
            <p className="text-gray-600 mt-1">
              Manage your practice staff and their preferences for lunch meetings
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={exportStaffList}
              className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Staff Member</span>
            </button>
          </div>
        </div>

        {/* Status Message */}
        {saveStatus && (
          <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
            saveStatus.includes('Error') 
              ? 'bg-red-50 border border-red-200 text-red-700' 
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {saveStatus.includes('Error') ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            <span>{saveStatus}</span>
          </div>
        )}
      </div>

      {/* Search and Filter */}
      <div className="mb-6 bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search staff members..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Roles</option>
              {commonRoles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-lg shadow-md">
        {filteredStaff.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {staffMembers.length === 0 ? 'No Staff Members Added' : 'No Staff Members Found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {staffMembers.length === 0 
                ? 'Add your practice staff members so sales representatives can include them in lunch meetings.'
                : 'Try adjusting your search criteria to find specific staff members.'
              }
            </p>
            {staffMembers.length === 0 && (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg mx-auto transition-colors"
              >
                <UserPlus className="w-5 h-5" />
                <span>Add Your First Staff Member</span>
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Name & Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Contact</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Preferences</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStaff.map((staff) => (
                  <tr key={staff.id} className="hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-gray-800">{staff.name}</p>
                        <p className="text-sm text-gray-600">{staff.role}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        {staff.email && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            <span>{staff.email}</span>
                          </div>
                        )}
                        {staff.phone && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            <span>{staff.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="space-y-1">
                        {staff.dietary_restrictions?.length > 0 && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Heart className="w-3 h-3 text-red-500" />
                            <span className="text-gray-600">
                              {staff.dietary_restrictions.slice(0, 2).join(', ')}
                              {staff.dietary_restrictions.length > 2 && ` +${staff.dietary_restrictions.length - 2} more`}
                            </span>
                          </div>
                        )}
                        {staff.favorite_cuisines?.length > 0 && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Utensils className="w-3 h-3 text-green-500" />
                            <span className="text-gray-600">
                              {staff.favorite_cuisines.slice(0, 2).join(', ')}
                              {staff.favorite_cuisines.length > 2 && ` +${staff.favorite_cuisines.length - 2} more`}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(staff)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(staff)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Staff Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">
                  {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Required Information */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-800 mb-3">Required Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter staff member's name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role *
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({...formData, role: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Select role</option>
                        {commonRoles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Optional Information - Collapsible */}
                <details className="border border-gray-200 rounded-lg">
                  <summary className="p-4 cursor-pointer bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <span className="font-medium text-gray-700">Optional Information (Click to expand)</span>
                    <span className="text-sm text-gray-500 ml-2">Contact details, preferences, etc.</span>
                  </summary>
                  <div className="p-4 space-y-4">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>

                    {/* Dietary Restrictions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Dietary Restrictions
                      </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {dietaryOptions.map(option => (
                      <label key={option} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.dietaryRestrictions.includes(option)}
                          onChange={() => handleArrayToggle(
                            formData.dietaryRestrictions, 
                            option, 
                            (newArray) => setFormData({...formData, dietaryRestrictions: newArray})
                          )}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Favorite Cuisines */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Favorite Cuisines
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {cuisineOptions.map(cuisine => (
                      <label key={cuisine} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.favoriteCuisines.includes(cuisine)}
                          onChange={() => handleArrayToggle(
                            formData.favoriteCuisines, 
                            cuisine, 
                            (newArray) => setFormData({...formData, favoriteCuisines: newArray})
                          )}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{cuisine}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Allergens */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Known Allergens
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {allergenOptions.map(allergen => (
                      <label key={allergen} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.allergens.includes(allergen)}
                          onChange={() => handleArrayToggle(
                            formData.allergens, 
                            allergen, 
                            (newArray) => setFormData({...formData, allergens: newArray})
                          )}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700">{allergen}</span>
                      </label>
                    ))}
                  </div>
                </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Any additional preferences or notes..."
                      />
                    </div>
                  </div>
                </details>

                {/* Form Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    💡 <strong>Quick tip:</strong> You can add just name and role, then edit details later
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      <span>{editingStaff ? 'Update Staff Member' : 'Add Staff Member'}</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && staffToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-start mb-6">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Remove Staff Member</h3>
                  <p className="text-gray-600">
                    Are you sure you want to remove <strong>{staffToDelete.name}</strong> from your staff list? 
                    This action cannot be undone.
                  </p>
                  {staffToDelete.orderHistory && staffToDelete.orderHistory.length > 0 && (
                    <p className="text-sm text-yellow-600 mt-2">
                      ⚠️ This staff member has {staffToDelete.orderHistory.length} order(s) in their history.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Remove Staff Member
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(StaffManagement);